"use client";

import { usePathname, useRouter } from "next/navigation";
import { useCallback, useMemo } from "react";
import { saveOverlayTransfer } from "@/features/overlays/lib/overlay-transfer.client";
import {
  STORY_COMPOSER_PATH,
  storyPathForViewer,
} from "@/features/overlays/lib/routes";
import type {
  OpenStoryComposerInput,
  OpenStoryHighlightInput,
  OpenStoryViewerInput,
} from "@/features/stories/lib/types";

/**
 * Route-driven Story overlay controls.
 *
 * The historic Provider rendered both heavy portals above every product page
 * and reimplemented browser history by hand. The root `@overlay` parallel slot
 * now owns rendering; this hook only records transient launch data and asks the
 * App Router to open the canonical Story route.
 */
export function useStoryViewer() {
  const router = useRouter();
  const pathname = usePathname();

  const openViewer = useCallback(
    (input: OpenStoryViewerInput) => {
      if (input.authors.length === 0) return;
      const path = storyPathForViewer(input);
      if (!path) return;
      saveOverlayTransfer(path, input);
      const storyId = input.startStoryId;
      router.push(
        storyId ? `${path}?storyId=${encodeURIComponent(storyId)}` : path,
        { scroll: false },
      );
    },
    [router],
  );

  const openHighlight = useCallback(
    (input: OpenStoryHighlightInput) => {
      openViewer({
        authors: [
          {
            type: "user",
            id: `highlight-${input.highlightId}`,
            name: input.title,
            avatarUrl: input.coverUrl ?? null,
            href: null,
            highlightId: input.highlightId,
          },
        ],
        startAuthorIndex: 0,
      });
    },
    [openViewer],
  );

  const openComposer = useCallback(
    (input: OpenStoryComposerInput = {}) => {
      saveOverlayTransfer(STORY_COMPOSER_PATH, input);
      router.push(STORY_COMPOSER_PATH, { scroll: false });
    },
    [router],
  );

  const close = useCallback(() => router.back(), [router]);

  return useMemo(
    () => ({
      openViewer,
      openHighlight,
      closeViewer: close,
      isViewerOpen:
        pathname.startsWith("/story/") && pathname !== STORY_COMPOSER_PATH,
      openComposer,
      closeComposer: close,
      isComposerOpen: pathname === STORY_COMPOSER_PATH,
    }),
    [close, openComposer, openHighlight, openViewer, pathname],
  );
}
